
import React, { useMemo, useState } from "react";
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { differenceInMinutes, parseISO, getHours, getDay, format, startOfMonth, endOfMonth } from "date-fns";
import { CalendarEvent, CalendarGuard, GuardWorkingHoursData } from "@/pages/scheduling/types";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, Building, Moon, Calendar, ChevronDown, ChevronRight, ArrowUp, ArrowDown, RefreshCw } from "lucide-react";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { fetchAllGuardSchedules } from "../services/totalWorkService";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { calculateOvertimeHours } from "@/pages/blank-page/utils/overtimeUtils";
import { useIsMobile } from "@/hooks/use-mobile";
import { calculateShiftDuration } from "@/pages/schedule-alerts/services/utils/dateUtils";
import { useUserRoles } from "@/hooks/useUserRoles";
import GuardCostsTable from "./GuardCostsTable";

const LEAVE_TITLES = [
  "ΚΑΝ ΑΔΕΙΑ ΠΡΟΗΓ ΕΤΟΥΣ",
  "ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ",
  "ΜΕ",
  "ΑΝΑΡΡΩΤΙΚΗ ΑΔΕΙΑ"
];

const isLeaveShift = (title: string): boolean => {
  return LEAVE_TITLES.some(leaveTitle => 
    title.toUpperCase().trim() === leaveTitle.toUpperCase().trim()
  );
};

const isWeekday = (date: Date): boolean => {
  const day = getDay(date);
  return day >= 1 && day <= 5;
};

const calculateNightMinutes = (startDate: Date, endDate: Date): number => {
  let currentDate = new Date(startDate);
  let totalNightMinutes = 0;
  
  while (currentDate < endDate) {
    const hour = getHours(currentDate);
    const isNightHour = hour >= 22 || hour < 6;
    
    if (isNightHour) {
      totalNightMinutes += 1;
    }
    
    currentDate = new Date(currentDate.getTime() + 60000);
    
    if (totalNightMinutes > 24 * 60) {
      console.error("Night hours calculation exceeded 24 hours, stopping to prevent infinite loop");
      break;
    }
  }
  
  return totalNightMinutes;
};

const isDateOnSunday = (date: Date): boolean => {
  return getDay(date) === 0;
};

const isInHolidayPeriod = (startDate: Date, endDate: Date, holidays: string[]): boolean => {
  let currentDate = new Date(startDate);
  const startDay = format(startDate, 'yyyy-MM-dd');
  const endDay = format(endDate, 'yyyy-MM-dd');
  
  if (startDay === endDay) {
    return isDateOnSunday(startDate) || holidays.includes(startDay);
  }
  
  while (currentDate < endDate) {
    const currentDay = format(currentDate, 'yyyy-MM-dd');
    if (isDateOnSunday(currentDate) || holidays.includes(currentDay)) {
      return true;
    }
    currentDate.setDate(currentDate.getDate() + 1);
    currentDate.setHours(0, 0, 0, 0);
  }
  
  return false;
};

const calculateHolidayMinutes = (startDate: Date, endDate: Date, holidays: string[], shiftTitle: string): number => {
  if (isLeaveShift(shiftTitle)) {
    return 0;
  }
  
  let currentDate = new Date(startDate);
  let totalHolidayMinutes = 0;
  
  while (currentDate < endDate) {
    const currentDay = format(currentDate, 'yyyy-MM-dd');
    const isSunday = isDateOnSunday(currentDate);
    const isHoliday = holidays.includes(currentDay);
    
    if (isSunday || isHoliday) {
      totalHolidayMinutes += 1;
    }
    
    currentDate = new Date(currentDate.getTime() + 60000);
    
    if (totalHolidayMinutes > 24 * 60 * 7) {
      console.error("Holiday hours calculation exceeded 1 week, stopping to prevent infinite loop");
      break;
    }
  }
  
  return totalHolidayMinutes;
};

const isSickLeave = (title: string): boolean => {
  return title.toUpperCase().includes('ΑΝΑΡΡΩΤΙΚΗ') || 
         title.toUpperCase().includes('SICK') || 
         title.toUpperCase().includes('MEDICAL');
};

interface GuardWorkingHoursProps {
  events: CalendarEvent[] | undefined;
  guards: CalendarGuard[] | undefined;
  userIsAdmin?: boolean;
  teamId: string | null;
  dateRange?: { start: Date; end: Date };
}

interface GuardHours {
  guardId: string;
  name: string;
  totalMinutes: number;
  nightMinutes: number;
  holidayMinutes: number;
  sickLeaveMinutes: number;
  totalHours: number;
  nightHours: number;
  holidayHours: number;
  sickLeaveHours: number;
  shiftsCount: number;
  teams: Record<string, TeamHoursData>;
}

interface TeamHoursData {
  hours: number;
  nightHours: number;
  holidayHours: number;
  sickLeaveHours: number;
  shifts: number;
}

const GuardWorkingHours: React.FC<GuardWorkingHoursProps> = ({ 
  events, 
  guards, 
  userIsAdmin = false,
  teamId,
  dateRange
}) => {
  const { t } = useLanguage();
  const { user } = useSupabaseUser();
  const [expandedGuards, setExpandedGuards] = useState<Record<string, boolean>>({});
  const [expandedTeams, setExpandedTeams] = useState<Record<string, boolean>>({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isMobile = useIsMobile();
  const { isAdmin } = useUserRoles();

  const isMonthView = useMemo(() => {
    if (!dateRange) return false;
    
    const startMonth = dateRange.start.getMonth();
    const startYear = dateRange.start.getFullYear();
    const monthStart = startOfMonth(new Date(startYear, startMonth));
    const monthEnd = endOfMonth(new Date(startYear, startMonth));
    
    const isApproxMonthStart = Math.abs(dateRange.start.getTime() - monthStart.getTime()) < 3 * 24 * 60 * 60 * 1000;
    const isApproxMonthEnd = Math.abs(dateRange.end.getTime() - monthEnd.getTime()) < 3 * 24 * 60 * 60 * 1000;
    
    return isApproxMonthStart && isApproxMonthEnd;
  }, [dateRange]);

  const { data: allTeamSchedules } = useQuery({
    queryKey: ['all-team-schedules', user?.id],
    queryFn: async (): Promise<CalendarEvent[]> => {
      if (!user) return [];
      
      try {
        if (userIsAdmin) {
          const { data, error } = await supabase
            .from('team_schedules')
            .select('*');
            
          if (error) {
            console.error('Error fetching all team schedules:', error);
            throw error;
          }
          
          return data || [];
        } 
        else {
          const { data, error } = await supabase
            .from('team_schedules')
            .select('*')
            .contains('assigned_guards', [user.id]);
            
          if (error) {
            console.error('Error fetching user schedules:', error);
            throw error;
          }
          
          return data || [];
        }
      } catch (error: any) {
        console.error('Error in fetchAllTeamSchedules:', error);
        return [];
      }
    },
    enabled: !!user,
  });

  const { data: allGuards } = useQuery({
    queryKey: ['all-guards'],
    queryFn: async (): Promise<CalendarGuard[]> => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, full_name')
          .eq('status', 'active');
          
        if (error) {
          console.error('Error fetching all guards:', error);
          throw error;
        }
        
        // Properly map database results to CalendarGuard type
        return (data || []).map(profile => ({
          id: profile.id,
          name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.full_name || profile.id.substring(0, 8),
          fullName: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.full_name || profile.id.substring(0, 8),
          first_name: profile.first_name || '',
          last_name: profile.last_name || '',
          full_name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.full_name || profile.id.substring(0, 8)
        }));
      } catch (error: any) {
        console.error('Error in fetchAllGuards:', error);
        return [];
      }
    },
  });

  const { data: allTeams } = useQuery({
    queryKey: ['all-teams-for-hours'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('teams')
          .select('id, name');
          
        if (error) {
          console.error('Error fetching teams:', error);
          throw error;
        }
        
        return data.reduce((acc: Record<string, string>, team) => {
          acc[team.id] = team.name;
          return acc;
        }, {});
      } catch (error: any) {
        console.error('Error in fetchTeams:', error);
        return {};
      }
    },
  });

  const { data: holidays } = useQuery({
    queryKey: ['holidays-for-hours'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('holidays')
          .select('date');
          
        if (error) {
          console.error('Error fetching holidays:', error);
          throw error;
        }
        
        console.log('Fetched holidays:', data);
        return data.map(h => h.date) || [];
      } catch (error: any) {
        console.error('Error in fetchHolidays:', error);
        return [];
      }
    },
  });

  const { data: guardTeamSchedules, isLoading: isLoadingGuardSchedules, refetch: refetchGuardSchedules } = useQuery({
    queryKey: ['guard-team-schedules', teamId, dateRange],
    queryFn: async (): Promise<Record<string, CalendarEvent[]>> => {
      if (!guards || guards.length === 0) return {};
      
      const result: Record<string, CalendarEvent[]> = {};
      
      for (const guard of guards) {
        if (userIsAdmin || guard.id === user?.id) {
          const schedules = await fetchAllGuardSchedules(
            guard.id, 
            dateRange ? { start: dateRange.start, end: dateRange.end } : undefined
          );
          result[guard.id] = schedules;
        }
      }
      
      return result;
    },
    enabled: !!guards && guards.length > 0,
  });

  const guardHours = useMemo(() => {
    const guardsToUse = guards || allGuards || [];
    const holidayDates = holidays || [];
    
    console.log('Calculating guard hours with:', { 
      guardsCount: guardsToUse.length,
      holidaysCount: holidayDates.length,
      teamId,
      dateRange: dateRange ? `${format(dateRange.start, 'yyyy-MM-dd')} to ${format(dateRange.end, 'yyyy-MM-dd')}` : 'undefined'
    });
    
    if (!guardsToUse.length) {
      console.log('No guards available for hours calculation');
      return [];
    }

    const hoursMap = new Map<string, GuardHours>();

    for (const guard of guardsToUse) {
      if (!userIsAdmin && user?.id !== guard.id) continue;
      
      let schedulesToProcess: CalendarEvent[] = [];
      
      if (guardTeamSchedules && guardTeamSchedules[guard.id]) {
        schedulesToProcess = guardTeamSchedules[guard.id];
        console.log(`[HOURS] Processing ${schedulesToProcess.length} schedules from all teams for guard ${guard.full_name || guard.id}`);
      } else {
        schedulesToProcess = allTeamSchedules || [];
        schedulesToProcess = schedulesToProcess.filter(schedule => 
          schedule.assigned_guards && schedule.assigned_guards.includes(guard.id)
        );
        console.log(`[HOURS] Fallback: Processing ${schedulesToProcess.length} schedules for guard ${guard.full_name || guard.id}`);
      }
      
      if (dateRange) {
        schedulesToProcess = schedulesToProcess.filter(event => {
          const eventStart = fromLocalISOString(event.start_date);
          const eventEnd = fromLocalISOString(event.end_date);
          return (eventStart <= dateRange.end && eventEnd >= dateRange.start);
        });
      }
      
      if (guard.full_name && guard.full_name.includes("ΜΠΙΜΠΙΖΑ")) {
        console.log(`[DEBUG] Processing ${schedulesToProcess.length} schedules for guard ${guard.full_name}`);
        schedulesToProcess.forEach(s => {
          console.log(`  Schedule: ${s.title}, Start: ${format(fromLocalISOString(s.start_date), 'yyyy-MM-dd HH:mm')} End: ${format(fromLocalISOString(s.end_date), 'yyyy-MM-dd HH:mm')}`);
        });
      }
      
      if (!hoursMap.has(guard.id)) {
        let displayName = '';
        
        if (guard.full_name && guard.full_name.trim() !== '') {
          displayName = guard.full_name;
        } else if (guard.first_name || guard.last_name) {
          displayName = `${guard.first_name || ''} ${guard.last_name || ''}`.trim();
        } else {
          displayName = guard.id.substring(0, 8);
        }
        
        hoursMap.set(guard.id, {
          guardId: guard.id,
          name: displayName,
          totalMinutes: 0,
          nightMinutes: 0,
          holidayMinutes: 0,
          sickLeaveMinutes: 0,
          totalHours: 0,
          nightHours: 0,
          holidayHours: 0,
          sickLeaveHours: 0,
          shiftsCount: 0,
          teams: {}
        });
      }
      
      for (const schedule of schedulesToProcess) {
        const startDate = fromLocalISOString(schedule.start_date);
        const endDate = fromLocalISOString(schedule.end_date);
        
        // Skip excluded shifts (marked with (EXC) in title or training sessions)
        if (schedule.title && schedule.title.includes('(EXC)')) {
          if (guard.full_name && guard.full_name.includes("ΜΠΙΜΠΙΖΑ")) {
            console.log(`[EXCLUDED SHIFT] Skipping hours calculation for: ${schedule.title}`);
          }
          continue;
        }
        
        // Also skip training sessions by description (for backwards compatibility)
        if (schedule.description && schedule.description.startsWith('TRAINING_SESSION:')) {
          if (guard.full_name && guard.full_name.includes("ΜΠΙΜΠΙΖΑ")) {
            console.log(`[TRAINING SESSION] Skipping hours calculation for: ${schedule.title}`);
          }
          continue;
        }
        
        const isRepoShift = (schedule.title || '').toUpperCase().includes('ΡΕΠΟ') || 
                            (schedule.title || '').toUpperCase().includes('REPO') ||
                            (schedule.title || '').toUpperCase().includes('OFF');
                          
        if (isRepoShift) {
          if (guard.full_name && guard.full_name.includes("ΜΠΙΜΠΙΖΑ")) {
            console.log(`[REPO SHIFT] Skipping hours calculation for: ${schedule.title}`);
          }
          continue;
        }
        
        const durationHours = calculateShiftDuration(startDate, endDate);
        const durationMinutes = durationHours * 60;
        
        if (guard.full_name && guard.full_name.includes("ΜΠΙΜΠΙΖΑ")) {
          console.log(`[SHIFT DURATION] ${schedule.title}: ${durationHours} hours (${durationMinutes} minutes)`);
        }
        
        const nightMinutes = calculateNightMinutes(startDate, endDate);
        const holidayMinutes = calculateHolidayMinutes(startDate, endDate, holidayDates, schedule.title || '');
        
        const isSickLeaveSchedule = LEAVE_TITLES.some(title => 
          (schedule.title || '').toUpperCase().includes(title)
        ) || isSickLeave(schedule.title || '');
        
        const scheduleTeamId = schedule.team_id;
        
        const guardData = hoursMap.get(guard.id)!;
        
        guardData.totalMinutes += durationMinutes;
        guardData.nightMinutes += nightMinutes;
        guardData.holidayMinutes += holidayMinutes;
        guardData.shiftsCount += 1;
        
        if (isSickLeaveSchedule) {
          guardData.sickLeaveMinutes += durationMinutes;
          console.log(`[SICK LEAVE] Counted ${durationMinutes/60} hours of sick leave for ${guardData.name}`);
        }
        
        if (!guardData.teams[scheduleTeamId]) {
          guardData.teams[scheduleTeamId] = { 
            hours: 0, 
            nightHours: 0, 
            holidayHours: 0, 
            sickLeaveHours: 0,
            shifts: 0 
          };
        }
        
        guardData.teams[scheduleTeamId].shifts += 1;
        guardData.teams[scheduleTeamId].hours += durationMinutes / 60;
        guardData.teams[scheduleTeamId].nightHours += nightMinutes / 60;
        guardData.teams[scheduleTeamId].holidayHours += holidayMinutes / 60;
        
        if (isSickLeaveSchedule) {
          guardData.teams[scheduleTeamId].sickLeaveHours += durationMinutes / 60;
        }
        
        hoursMap.set(guard.id, guardData);
        
        if (guard.full_name && guard.full_name.includes("ΜΠΙΜΠΙΖΑ")) {
          console.log(`[SHIFT] ${schedule.title}: ${durationMinutes/60} hours (${durationMinutes} minutes)`);
          console.log(`  New total: ${guardData.totalMinutes/60} hours`);
        }
        
        const dayOfWeek = getDay(startDate);
        const isSunday = dayOfWeek === 0; // Only Sundays are holidays
        if (isSunday) {
          console.log(`[HOLIDAY SHIFT] Processing shift ${schedule.title} on ${format(startDate, 'EEEE')} for ${guardData.name}`);
        }
      }
    }

    const result = Array.from(hoursMap.values())
      .map(guard => ({
        ...guard,
        totalHours: guard.totalMinutes / 60,
        nightHours: guard.nightMinutes / 60,
        holidayHours: guard.holidayMinutes / 60,
        sickLeaveHours: guard.sickLeaveMinutes / 60,
        teams: Object.entries(guard.teams).reduce<Record<string, TeamHoursData>>((acc, [teamId, data]) => {
          acc[teamId] = {
            ...data,
            hours: data.hours,
            nightHours: data.nightHours,
            holidayHours: data.holidayHours,
            sickLeaveHours: data.sickLeaveHours,
          };
          return acc;
        }, {})
      }))
      .sort((a, b) => b.totalHours - a.totalHours);
    
    const mpimpizaData = result.find(guard => guard.name.includes("ΜΠΙΜΠΙΖΑ"));
    if (mpimpizaData) {
      console.log(`[FINAL] MPIMPIZA total hours: ${mpimpizaData.totalHours} from ${mpimpizaData.totalMinutes/60} minutes`);
    }
    
    console.log('Calculated guard hours:', { count: result.length });
    return result;
  }, [events, guards, userIsAdmin, user?.id, holidays, teamId, dateRange, allTeamSchedules, allGuards, guardTeamSchedules]);

  const guardHoursData: GuardWorkingHoursData[] = useMemo(() => {
    return guardHours.map(guard => {
      // Use the full name instead of processing it
      let displayName = guard.name;
      
      let overtime = 0;
      
      if (isMonthView) {
        const monthStartDate = dateRange ? startOfMonth(dateRange.start) : startOfMonth(new Date());
        
        overtime = calculateOvertimeHours(
          guardTeamSchedules?.[guard.guardId] || [], 
          monthStartDate
        );
      } else {
        overtime = guard.totalHours - guard.sickLeaveHours - 40;
      }
      
      return {
        guardId: guard.guardId,
        name: displayName,
        hours: guard.totalHours,
        nightHours: guard.nightHours,
        holidayHours: guard.holidayHours,
        sickLeaveHours: guard.sickLeaveHours,
        shifts: guard.shiftsCount,
        overtime: Math.round(overtime),
        teamBreakdown: Object.entries(guard.teams).map(([teamId, data]) => ({
          teamId,
          teamName: allTeams?.[teamId] || teamId,
          hours: data.hours,
          nightHours: data.nightHours,
          holidayHours: data.holidayHours,
          sickLeaveHours: data.sickLeaveHours,
          shifts: data.shifts
        }))
      };
    });
  }, [guardHours, allTeams, guardTeamSchedules, dateRange, isMonthView]);

  const toggleGuardExpand = (guardId: string) => {
    setExpandedGuards(prev => ({
      ...prev,
      [guardId]: !prev[guardId]
    }));
  };

  const toggleTeamExpand = (teamId: string) => {
    setExpandedTeams(prev => ({
      ...prev,
      [teamId]: !prev[teamId]
    }));
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await refetchGuardSchedules();
      toast.success(t("dataRefreshed") || "Working hours data refreshed");
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error(t("refreshError") || "Could not refresh data. Please try again.");
    } finally {
      setIsRefreshing(false);
    }
  };

  const titleElement = (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1.5">
      <div className="flex items-center">
        <Clock className="h-5 w-5 text-primary mr-2" />
        <span className="text-base sm:text-lg">
          {teamId 
            ? (t("guardWorkingHoursForTeam") || "Personnel Working Hours") 
            : (t("guardTotalWorkingHours") || "Personnel Total Working Hours")}
        </span>
      </div>
      {!teamId && 
        <span className="text-xs sm:text-sm text-muted-foreground ml-0 sm:ml-2">
          {t("acrossAllTeams") || "(across all teams)"}
        </span>
      }
      {dateRange && (
        <span className="text-xs sm:text-sm text-muted-foreground ml-0 sm:ml-2 mt-1 sm:mt-0">
          ({format(dateRange.start, 'MMM d')} - {format(dateRange.end, 'MMM d, yyyy')})
        </span>
      )}
    </div>
  );

  return (
    <Card className="w-full bg-white dark:bg-background shadow-md rounded-lg border">
      <CardHeader className="pb-2 border-b px-2 sm:px-4 md:px-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <CardTitle className="text-base sm:text-lg">
            {titleElement}
          </CardTitle>
          <Button 
            variant="outline" 
            size="sm" 
            className="flex items-center gap-1.5 self-end sm:self-auto"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
            <span className="text-xs sm:text-sm">
              {isRefreshing 
                ? (t("refreshing") || "Refreshing...") 
                : (t("refresh") || "Refresh")}
            </span>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {guardHoursData.length === 0 ? (
          <div className="text-center py-12 px-6 bg-gradient-to-br from-muted/20 to-muted/5 rounded-lg mx-6 my-6">
            <div className="animate-fade-in">
              <Building className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              {events && events.length > 0 && guards && guards.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-lg font-medium text-foreground">{t("noGuardsAssigned") || "Δεν έχουν ανατεθεί φύλακες ακόμα"}</p>
                  <p className="text-sm text-muted-foreground">{t("assignGuardsToSeeHours") || "Αναθέστε φύλακες για να δείτε τις ώρες εργασίας"}</p>
                </div>
              ) : (
                <p className="text-lg font-medium text-foreground">{t("noWorkingHoursData") || "Δεν υπάρχουν διαθέσιμες ώρες εργασίας για την επιλεγμένη περίοδο"}</p>
              )}
            </div>
          </div>
        ) : (
          <div className="w-full overflow-hidden animate-fade-in">
            <ScrollArea className="w-full h-[600px]">
              <div className="p-6">
                <div className={cn("min-w-[600px] max-w-full", isMobile ? "pr-4" : "")}>
                  <div className="rounded-lg border border-border/50 bg-gradient-to-r from-background to-muted/10 overflow-hidden shadow-sm">
                    <Table className="w-full">
                      <TableHeader>
                        <TableRow className="bg-gradient-to-r from-muted/30 to-muted/20 hover:from-muted/40 hover:to-muted/30 transition-all duration-200">
                          <TableHead className="w-[100px] sm:w-[150px] font-semibold text-foreground py-4 px-6">
                            <div className="flex items-center gap-2">
                              <Building className="h-4 w-4 text-primary" />
                              {t("guard") || "Guard"}
                            </div>
                          </TableHead>
                          <TableHead className="text-center w-[60px] sm:w-[100px] font-semibold text-foreground py-4">
                            <div className="flex flex-col items-center gap-1">
                              <Clock className="h-4 w-4 text-primary" />
                              <span className="text-xs">{t("totalHours") || "Total Hours"}</span>
                            </div>
                          </TableHead>
                          <TableHead className="text-center w-[80px] sm:w-[120px] font-semibold text-foreground py-4">
                            <div className="flex flex-col items-center gap-1">
                              <Moon className="h-4 w-4 text-indigo-500" />
                              <span className="text-xs">{t("nightHours") || "Night Hours"}</span>
                            </div>
                          </TableHead>
                          <TableHead className="text-center w-[80px] sm:w-[120px] font-semibold text-foreground py-4">
                            <div className="flex flex-col items-center gap-1">
                              <Calendar className="h-4 w-4 text-orange-500" />
                              <span className="text-xs">{t("holidayHours") || "Holiday Hours"}</span>
                            </div>
                          </TableHead>
                          <TableHead className="text-center w-[80px] sm:w-[100px] font-semibold text-foreground py-4">
                            <div className="flex flex-col items-center gap-1">
                              <Clock className="h-4 w-4 text-emerald-500" />
                              <span className="text-xs">
                                {isMonthView 
                                  ? (t("monthOvertime") || "Overtime")
                                  : (t("weeklyHrsBalance") || "Balance")}
                              </span>
                            </div>
                          </TableHead>
                          <TableHead className="w-[220px] sm:w-[330px] font-semibold text-foreground py-4">
                            <div className="flex items-center gap-2">
                              <ChevronDown className="h-4 w-4 text-primary" />
                              {t("breakdown") || "Breakdown"}
                            </div>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {guardHoursData.map((guard, index) => (
                          <TableRow 
                            key={guard.guardId} 
                            className="hover:bg-muted/20 transition-all duration-200 group border-b border-border/30"
                            style={{ animationDelay: `${index * 50}ms` }}
                          >
                            <TableCell className="font-medium py-4 px-6">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center border border-primary/20">
                                  <span className="text-xs font-semibold text-primary">
                                    {guard.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                                  </span>
                                </div>
                                <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                                  {guard.name}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center py-4">
                              <div className="flex flex-col items-center gap-1">
                                <span className="text-lg font-bold text-foreground bg-gradient-to-r from-primary/10 to-primary/5 px-3 py-1 rounded-full border border-primary/20">
                                  {guard.hours}
                                </span>
                                {guard.sickLeaveHours > 0 && (
                                  <span className="text-xs text-red-500 bg-red-50 dark:bg-red-950/20 px-2 py-0.5 rounded-full border border-red-200 dark:border-red-800">
                                    ({guard.hours - guard.sickLeaveHours} {t("effective") || "effective"})
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center py-4">
                              <span className="inline-flex items-center justify-center text-sm font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/20 px-3 py-1.5 rounded-full border border-indigo-200 dark:border-indigo-800">
                                <Moon className="h-3 w-3 mr-1" />
                                {guard.nightHours}
                              </span>
                            </TableCell>
                            <TableCell className="text-center py-4">
                              <span className="inline-flex items-center justify-center text-sm font-bold text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/20 px-3 py-1.5 rounded-full border border-orange-200 dark:border-orange-800">
                                <Calendar className="h-3 w-3 mr-1" />
                                {guard.holidayHours}
                              </span>
                            </TableCell>
                            <TableCell className="text-center py-4">
                              <div className={cn(
                                "inline-flex items-center justify-center px-3 py-1.5 rounded-full border font-semibold text-sm transition-all duration-200",
                                guard.overtime > 0 
                                  ? "text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800" 
                                  : guard.overtime < 0 
                                  ? "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800" 
                                  : "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800"
                              )}>
                                {guard.overtime !== 0 && (
                                  guard.overtime > 0 ? (
                                    <ArrowUp className="h-3 w-3 mr-1" />
                                  ) : (
                                    <ArrowDown className="h-3 w-3 mr-1" />
                                  )
                                )}
                                <span>{Math.abs(guard.overtime)}</span>
                              </div>
                            </TableCell>
                            <TableCell className="py-4">
                              <div className="text-sm">
                                {guard.teamBreakdown && guard.teamBreakdown.length > 1 ? (
                                  <div className="space-y-2">
                                    {guard.teamBreakdown.map(team => (
                                      <div key={team.teamId} className="flex items-center justify-between p-2 bg-muted/30 rounded-md border border-border/30">
                                        <span className="font-medium text-foreground truncate max-w-[120px] sm:max-w-[180px]">{team.teamName}</span>
                                        <span className="font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full text-xs">
                                          {team.hours}h
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-center py-3 px-4 bg-muted/20 rounded-md border border-dashed border-border/50">
                                    <span className="text-sm text-muted-foreground">
                                      {guard.teamBreakdown?.[0]?.teamName || t("noTeamsAssigned") || "No teams"}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </ScrollArea>
          </div>
        )}

        {isAdmin && guardHoursData.length > 0 && (
          <GuardCostsTable guardsData={guardHoursData} />
        )}
      </CardContent>
    </Card>
  );
};

export default GuardWorkingHours;
